// src/views/Inventory/StockOpname/StockOpnameForm.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Table,
  Space,
  InputNumber,
  Popconfirm,
  message,
  Tag,
  Modal,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../../configs"; // SESUAIKAN PATH

const { TextArea } = Input;
const { Option } = Select;

export default function StockOpnameForm() {
  const [form] = Form.useForm();

  const { id } = useParams(); // id_stock_opname saat edit/detail
  const location = useLocation();

  const isAddMode = location.pathname.includes("/add");
  const isDetailMode = location.pathname.includes("/detail");
  const isEditMode = location.pathname.includes("/edit");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [status, setStatus] = useState(0);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // item stock data (from item_stock table)
  const [itemStockList, setItemStockList] = useState([]);
  const [loadingItemStock, setLoadingItemStock] = useState(false);

  // ====== FETCH ITEM STOCK (bukan item master) ======
  const fetchItemStock = async () => {
    setLoadingItemStock(true);
    try {
      const res = await axiosInstance.get("/item-stock", {
        params: { limit: 5000 },
      });
      const data = res.data?.data || [];
      // Each item_stock record has: id_item_stock, item_code, item_name, lokasi_lot, jumlah_kemasan, kemasan, nopen, seri
      setItemStockList(data);
    } catch (err) {
      console.error("Failed fetch item stock", err);
      message.error("Gagal memuat data stok barang");
    } finally {
      setLoadingItemStock(false);
    }
  };

  // ====== FETCH DATA SAAT EDIT/DETAIL ======
  const fetchDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/stock-opname/${id}/with-details`);
      const { header, details } = res.data;

      setStatus(header.status);

      form.setFieldsValue({
        adjustment_number: header.adjustment_number,
        adjustment_date: header.adjustment_date
          ? dayjs(header.adjustment_date)
          : null,
        id_warehouse: header.id_warehouse,
        id_customer: header.id_customer,
        notes: header.notes,
      });

      setDetailRows(
        (details || []).map((d) => ({
          key: d.id_stock_opname_detail,
          ...d,
        })),
      );
    } catch (err) {
      console.error("Failed fetch stock opname detail", err);
      message.error("Gagal mengambil data stock opname");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemStock();
    if (!isAddMode) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ====== HANDLE DETAIL ROWS ======
  const addDetailRow = () => {
    const newRow = {
      key: Date.now(),
      id_item_stock: null,
      item_code: null,
      item_name: "",
      lokasi_lot: "",
      satuan: "",
      qty_system: 0,
      qty_physical: 0,
      qty_diff: 0,
      notes: "",
    };
    setDetailRows((prev) => [...prev, newRow]);
  };

  const removeDetailRow = (key) => {
    setDetailRows((prev) => prev.filter((row) => row.key !== key));
  };

  // Handle item stock selection - auto fill fields
  const handleItemStockSelect = (idItemStock, rowKey) => {
    const selected = itemStockList.find((s) => s.id_item_stock === idItemStock);
    if (!selected) return;

    setDetailRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          id_item_stock: idItemStock,
          item_code: selected.item_code,
          item_name: selected.item_name || "",
          nopen: selected.nopen || "",
          lokasi_lot: selected.lokasi_lot || "",
          satuan: selected.kemasan || "",
          qty_system: selected.jumlah_kemasan || 0,
          qty_physical: 0,
          qty_diff: -(selected.jumlah_kemasan || 0),
        };
      }),
    );
  };

  const updateDetailRow = (key, field, value) => {
    setDetailRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const updated = { ...row, [field]: value };

        // auto hitung qty_diff kalau ubah qty_physical
        if (field === "qty_physical") {
          const sys = Number(updated.qty_system || 0);
          const phy = Number(value || 0);
          updated.qty_diff = phy - sys;
        }

        return updated;
      }),
    );
  };

  // Generate options for Select - show item + lot info
  const itemStockOptions = itemStockList.map((s) => ({
    value: s.id_item_stock,
    label: `${s.item_code} - ${s.item_name || ""} | Lot: ${s.lokasi_lot || "-"} | Stok: ${s.jumlah_kemasan || 0}`,
    searchText: `${s.item_code} ${s.item_name || ""} ${s.lokasi_lot || ""}`,
  }));

  const columnsDetail = [
    {
      title: "No.",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Barang (dari Stok)",
      dataIndex: "id_item_stock",
      width: 350,
      render: (value, record) =>
        isDetailMode ? (
          <span>
            {record.item_code} - {record.item_name} | Lot: {record.lokasi_lot}
          </span>
        ) : (
          <Select
            showSearch
            allowClear
            placeholder="Pilih Barang dari Stok"
            size="small"
            style={{ width: "100%" }}
            value={value}
            loading={loadingItemStock}
            onChange={(val) => handleItemStockSelect(val, record.key)}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {itemStockOptions.map((it) => (
              <Option key={it.value} value={it.value}>
                {it.label}
              </Option>
            ))}
          </Select>
        ),
    },
    {
      title: "Lokasi Lot",
      dataIndex: "lokasi_lot",
      width: 100,
      render: (value) => <Tag color="geekblue">{value || "-"}</Tag>,
    },
    {
      title: "Satuan",
      dataIndex: "satuan",
      width: 80,
      render: (value) => value || "-",
    },
    {
      title: "Qty System",
      dataIndex: "qty_system",
      width: 100,
      align: "right",
      render: (value) => <strong>{value || 0}</strong>,
    },
    {
      title: "Qty Fisik",
      dataIndex: "qty_physical",
      width: 110,
      align: "right",
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <InputNumber
            size="small"
            min={0}
            style={{ width: "100%" }}
            value={value}
            onChange={(val) =>
              updateDetailRow(record.key, "qty_physical", val || 0)
            }
          />
        ),
    },
    {
      title: "Selisih",
      dataIndex: "qty_diff",
      width: 100,
      align: "right",
      render: (value) => {
        const num = Number(value || 0);
        const color = num === 0 ? "default" : num > 0 ? "green" : "red";
        const label = num === 0 ? "0" : num > 0 ? `+${num}` : `${num}`;
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Keterangan",
      dataIndex: "notes",
      width: 150,
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <Input
            size="small"
            value={value}
            onChange={(e) =>
              updateDetailRow(record.key, "notes", e.target.value)
            }
          />
        ),
    },
    !isDetailMode && {
      title: "",
      key: "action",
      width: 50,
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Hapus baris?"
          onConfirm={() => removeDetailRow(record.key)}
        >
          <Button danger icon={<DeleteOutlined />} size="small" type="text" />
        </Popconfirm>
      ),
    },
  ].filter(Boolean);

  const handleSubmit = (values) => {
    if (detailRows.length === 0) {
      message.warning("Detail barang minimal 1 baris");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;

    try {
      setSaving(true);

      const payload = {
        header: {
          adjustment_number: values.adjustment_number,
          adjustment_date: values.adjustment_date
            ? values.adjustment_date.format("YYYY-MM-DD")
            : null,
          status: status, // biasanya 0 (draft)
          notes: values.notes || null,
        },
        details: detailRows.map((row) => ({
          nopen: row.nopen,
          item_code: row.item_code,
          lokasi_lot: row.lokasi_lot || "",
          satuan: row.satuan || "",
          qty_system: Number(row.qty_system || 0),
          qty_physical: Number(row.qty_physical || 0),
          qty_diff:
            row.qty_diff != null
              ? Number(row.qty_diff)
              : Number(row.qty_physical || 0) - Number(row.qty_system || 0),
          notes: row.notes || null,
        })),
        id_customer: values.id_customer || null,
      };

      await axiosInstance.post("/adjustment", payload);
      window.location.href = `#/other/adjustment`;
    } catch (err) {
      console.error("Failed save stock opname", err);
      const msg =
        err?.response?.data?.error ||
        "Gagal menyimpan stock opname, cek log server";
      message.error(msg);
    } finally {
      setSaving(false);
      setConfirmVisible(false);
    }
  };

  return (
    <div className="py-4">
      <Card
        title={
          <Space>
            <span style={{ fontWeight: 600 }}>
              {isAddMode
                ? "Tambah Adjustment"
                : isDetailMode
                  ? "Detail Adjustment"
                  : "Edit Adjustment"}
            </span>
            {!isAddMode && (
              <Tag color={status === 1 ? "green" : "gold"}>
                {status === 1 ? "Final" : "Draft"}
              </Tag>
            )}
          </Space>
        }
        bodyStyle={{ padding: 16 }}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          initialValues={{
            status: 0,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="No. Adjustment"
                name="adjustment_number"
                rules={[
                  { required: true, message: "No. adjustment wajib diisi" },
                ]}
              >
                <Input
                  placeholder="Nomor dokumen adjustment"
                  disabled={isDetailMode}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tgl Adjustment"
                name="adjustment_date"
                rules={[
                  { required: true, message: "Tanggal adjustment wajib diisi" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabled={isDetailMode}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Catatan" name="notes">
            <TextArea rows={3} disabled={isDetailMode} />
          </Form.Item>

          <Card
            size="small"
            title={
              <Space>
                <span style={{ fontWeight: 500 }}>Detail Barang</span>
                <Tag color="blue">Total baris: {detailRows.length || 0}</Tag>
              </Space>
            }
            extra={
              !isDetailMode && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addDetailRow}
                >
                  Tambah Baris
                </Button>
              )
            }
            bodyStyle={{ padding: 8 }}
          >
            <Table
              rowKey="key"
              columns={columnsDetail}
              dataSource={detailRows}
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
            />
          </Card>

          {!isDetailMode && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <Button onClick={() => window.history.back()}>Batal</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={saving}
                style={{ background: "#3699FF", borderColor: "#3699FF" }}
              >
                Simpan
              </Button>
            </div>
          )}
        </Form>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Adjustment"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data adjustment ini?</p>
            <div
              style={{
                background: "#f5f5f5",
                padding: 10,
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>No Adjustment:</strong>{" "}
                {pendingValues.adjustment_number}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tanggal:</strong>{" "}
                {pendingValues.adjustment_date
                  ? pendingValues.adjustment_date.format("DD MMMM YYYY")
                  : "-"}
              </p>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>Detail Barang:</strong>
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  marginTop: 8,
                  border: "1px solid #f0f0f0",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    fontSize: 12,
                    borderCollapse: "collapse",
                  }}
                >
                  <thead
                    style={{
                      background: "#fafafa",
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          padding: "4px 8px",
                          textAlign: "left",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Item
                      </th>
                      <th
                        style={{
                          padding: "4px 8px",
                          textAlign: "left",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Lot
                      </th>
                      <th
                        style={{
                          padding: "4px 8px",
                          textAlign: "right",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Qty System
                      </th>
                      <th
                        style={{
                          padding: "4px 8px",
                          textAlign: "right",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Qty Fisik
                      </th>
                      <th
                        style={{
                          padding: "4px 8px",
                          textAlign: "right",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Selisih
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{ borderBottom: "1px solid #f0f0f0" }}
                      >
                        <td style={{ padding: "4px 8px" }}>{row.item_code}</td>
                        <td style={{ padding: "4px 8px" }}>{row.lokasi_lot}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>
                          {row.qty_system}
                        </td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>
                          {row.qty_physical}
                        </td>
                        <td
                          style={{
                            padding: "4px 8px",
                            textAlign: "right",
                            color: row.qty_diff !== 0 ? "red" : "inherit",
                          }}
                        >
                          {row.qty_diff}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
